import React from 'react';
import AudioPlayer from '../../../Utils/AudioPlayer';
import VideoPlayer from '../../../Utils/VideoPlayer';

/**
 * QuestionnaireAnswerCard Component
 * Displays the answer section for a questionnaire question
 * 
 * @param {Object} question - The question object
 * @param {boolean} showAnswers - Whether to show the answer section
 */
const QuestionnaireAnswerCard = ({ question, showAnswers = false }) => {
  
  if (!showAnswers) return null;

  // For empty answers, show a message
  if (!question.answer || 
      (question.type === 'multiple-answer' && Array.isArray(question.answer) && question.answer.length === 0)) {
    return (
      <div className="mt-4 border-t pt-3 border-gray-200">
        <h4 className="text-md font-medium text-gray-700 mb-2">Answer:</h4>
        <p className="text-gray-500 italic">No answer provided</p>
      </div>
    );
  }
  
  // Don't show placeholder answers
  if (question.answer === "[USER ANSWER APPENDED HERE]") return null;
  
  // Handle audio-answer type - primarily audio with optional text transcript
  if (question.type === 'audio-answer') {
    return (
      <div className="mt-4 border-t pt-3 border-gray-200">
        <h4 className="text-md font-medium text-gray-700 mb-2">Answer:</h4>
        {question.answer && typeof question.answer === 'string' && (
          <p className="text-gray-800 bg-blue-50 p-3 rounded-md mb-2 whitespace-pre-wrap">
            {question.answer}
          </p>
        )}
        <AudioPlayer file_key={question.file_key} />
      </div>
    );
  }
  
  // Handle video-answer type - primarily video with optional text transcript
  if (question.type === 'video-answer') {
    return (
      <div className="mt-4 border-t pt-3 border-gray-200">
        <h4 className="text-md font-medium text-gray-700 mb-2">Answer:</h4>
        {question.answer && typeof question.answer === 'string' && (
          <p className="text-gray-800 bg-blue-50 p-3 rounded-md mb-2 whitespace-pre-wrap">
            {question.answer}
          </p>
        )}
        <VideoPlayer file_key={question.file_key} />
      </div>
    );
  }
  
  // For multiple-answer questions, the answer might be an array
  if (question.type === 'multiple-answer' && Array.isArray(question.answer)) {
    return (
      <div className="mt-4 border-t pt-3 border-gray-200">
        <h4 className="text-md font-medium text-gray-700 mb-2">Answer:</h4>
        <div className="flex flex-row flex-wrap gap-2">
          {question.answer.map((ans, index) => (
            <div key={index} className="px-4 py-2 bg-blue-100 rounded-full text-blue-700">
              {ans}
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  // For single-answer questions
  if (question.type === 'single-answer' && typeof question.answer === 'string') {
    return (
      <div className="mt-4 border-t pt-3 border-gray-200">
        <h4 className="text-md font-medium text-gray-700 mb-2">Answer:</h4>
        <div className="px-4 py-2 bg-blue-100 rounded-full text-blue-700 inline-block">
          {question.answer}
        </div>
      </div>
    );
  }
  
  // For long-text and short-text questions
  if (['long-text', 'short-text'].includes(question.type) || typeof question.answer === 'string') {
    return (
      <div className="mt-4 border-t pt-3 border-gray-200">
        <h4 className="text-md font-medium text-gray-700 mb-2">Answer:</h4>
        <p className="text-gray-800 bg-blue-50 p-3 rounded-md">
          {question.answer}
        </p>
        <AudioPlayer file_key={question.file_key} />
      </div>
    );
  }
  
  // Fallback for any other type of answer
  return (
    <div className="mt-4 border-t pt-3 border-gray-200">
      <h4 className="text-md font-medium text-gray-700 mb-2">Answer:</h4>
      <p className="text-gray-800">
        {typeof question.answer === 'object' 
          ? JSON.stringify(question.answer) 
          : question.answer.toString()}
      </p>
    </div>
  );
};

export default QuestionnaireAnswerCard;
